/**
 * Sign-in screen rendered by the `Header` when the user is not yet
 * authenticated.
 *
 * Microsoft Entra ID (workforce) and Microsoft Entra External ID (CIAM) are
 * shown when the backend exposes at least one IdP via `/api/auth/config`.
 * Username + password (+ TOTP) is shown when `allowLocalLogin` from
 * `/api/auth/config` is true (`AUTH_ALLOW_LOCAL_LOGIN`).
 * When SSO is also configured, a divider separates the two options.
 *
 * Successful local login flows through `/api/authorization/authenticate`
 * which sets the same `cc.sid` cookie as the OIDC path — we then call
 * `completeLocalLogin(user)` to prime the in-memory snapshot.
 *
 * When 2FA is required but not yet configured (`requiresTwoFactorSetup`),
 * the pending session uses `/authorization/enroll2fa/*` before full login.
 */
import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import logo from '../assets/logo.png';
import { T } from '@/theme/tokens';
import { urls } from '@/urls';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { useAuth } from '@/auth/AuthProvider';
import type { UserDto } from '@cloudcost/shared-types/users';

type Step = 'credentials' | 'two-factor' | 'two-factor-enroll' | 'success';

interface LocalLoginResponse extends UserDto {
  requiresTwoFactor?: boolean;
  requiresTwoFactorSetup?: boolean;
  /** @deprecated use requiresTwoFactor; kept for backward compatibility */
  secret?: string | null;
}

interface Enroll2faStartResponse {
  hasBeenSetUp: boolean;
  qrcode: string | null;
  secret: string | null;
}

interface SignInScreenProps {
  errorBanner?: string | null;
}

export function SignInScreen({ errorBanner }: SignInScreenProps) {
  const { idps, allowLocalLogin, signInWithEntra, completeLocalLogin } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>('credentials');
  const [localUser, setLocalUser] = useState<LocalLoginResponse | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [enrollData, setEnrollData] = useState<Enroll2faStartResponse | null>(null);
  const [enrollLoading, setEnrollLoading] = useState(false);

  const hasSso = idps.length > 0;
  const showSso = hasSso && step === 'credentials';
  const showLocalLogin = allowLocalLogin && step === 'credentials';
  const ssoErrorBanner =
    errorBanner &&
    (!hasSso || !/no identity provider/i.test(errorBanner))
      ? errorBanner
      : null;

  const loadEnroll2fa = useCallback(async () => {
    setEnrollLoading(true);
    setLocalError(null);
    try {
      const data = (await fetchWrapper.post(
        urls.User.enroll2faStart,
        {},
      )) as Enroll2faStartResponse;
      if (data.hasBeenSetUp) {
        setStep('two-factor');
        setEnrollData(null);
        return;
      }
      if (!data.secret || !data.qrcode) {
        throw new Error('Could not start two-factor setup');
      }
      setEnrollData(data);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Could not start two-factor setup';
      setLocalError(message);
    } finally {
      setEnrollLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step === 'two-factor-enroll') {
      void loadEnroll2fa();
    }
  }, [step, loadEnroll2fa]);

  const handleEntra = (idpName?: string) => {
    setLocalError(null);
    signInWithEntra(idpName).catch((e) => {
      setLocalError(e?.message ?? 'Sign-in failed');
    });
  };

  const handleLocalLogin = async () => {
    setLocalError(null);
    setLoading(true);
    try {
      const res = await fetch(urls.User.authenticate, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        throw new Error('Invalid username or password');
      }
      const data = (await res.json()) as LocalLoginResponse;
      setLocalUser(data);
      if (data.requiresTwoFactorSetup) {
        setStep('two-factor-enroll');
      } else if (data.requiresTwoFactor) {
        setStep('two-factor');
      } else {
        completeLocalLogin(data);
        setStep('success');
      }
    } catch (e: unknown) {
      setLocalError(
        e instanceof Error ? e.message : 'Authentication failed',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactor = async () => {
    if (!localUser) return;
    setLocalError(null);
    setLoading(true);
    try {
      const user = (await fetchWrapper.post(urls.User.check2fa, {
        code,
      })) as UserDto;
      completeLocalLogin(user);
      setStep('success');
    } catch (e: unknown) {
      setLocalError(
        e instanceof Error ? e.message : 'Two-factor verification failed',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollConfirm = async () => {
    if (!enrollData?.secret) return;
    setLocalError(null);
    setLoading(true);
    try {
      const user = (await fetchWrapper.post(urls.User.enroll2faConfirm, {
        code,
        secret: enrollData.secret,
      })) as UserDto;
      completeLocalLogin(user);
      setStep('success');
    } catch (e: unknown) {
      setLocalError(
        e instanceof Error ? e.message : 'Two-factor setup failed',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: T.bg,
        p: 2,
      }}>
      <Box
        sx={{
          width: 420,
          maxWidth: '100%',
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: `${T.radius}px`,
          boxShadow: '0 12px 40px rgba(16,35,58,.10)',
          p: '36px 34px',
        }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src={logo} alt="IncreDevo" style={{ height: 40, marginBottom: 16 }} />
          <Typography sx={{ fontSize: 20, fontWeight: 900, color: T.text, letterSpacing: '-.3px' }}>
            Welcome to CloudCost
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.textFaint2, mt: 0.5 }}>
            Sign in to your cost optimization workspace
          </Typography>
        </Box>

        {ssoErrorBanner && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {ssoErrorBanner}
          </Alert>
        )}

        {showSso && (
          <Stack spacing={1.5}>
            {idps.map((entry) => (
              <Button
                key={entry.idp.name}
                fullWidth
                size="large"
                variant="contained"
                color="primary"
                data-testid={`entra-signin-${entry.idp.name}`}
                onClick={() => handleEntra(entry.idp.name)}>
                {entry.idp.name === 'entra-ciam'
                  ? 'Sign in (customer account)'
                  : 'Sign in with Microsoft'}
              </Button>
            ))}
          </Stack>
        )}

        {showLocalLogin && (
          <>
            {hasSso && (
              <Divider sx={{ my: 3, color: T.textFaint3, fontSize: 12, fontWeight: 700 }}>
                or use a password
              </Divider>
            )}
            <Stack spacing={2} sx={{ mt: hasSso ? 0 : 1 }}>
              <TextField
                label="Username"
                type="text"
                fullWidth
                size="small"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                inputProps={{ 'data-testid': 'login-username' }}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                size="small"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLocalLogin();
                }}
                autoComplete="current-password"
                inputProps={{ 'data-testid': 'login-password' }}
              />
              {localError && <Alert severity="error">{localError}</Alert>}
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={handleLocalLogin}
                disabled={loading}
                color="primary"
                data-testid="login-submit">
                {loading ? 'Loading...' : 'Sign in with password'}
              </Button>
            </Stack>
          </>
        )}

        {step === 'two-factor-enroll' && (
          <Stack spacing={2} sx={{ mt: 1 }} data-testid="two-factor-enroll">
            <Typography sx={{ fontSize: 15, fontWeight: 900, color: T.text }}>
              Set up two-factor authentication
            </Typography>
            <Typography sx={{ fontSize: 13, color: T.textFaint2, m: 0 }}>
              Scan the QR code with your authenticator app, then enter the generated
              code to finish signing in.
            </Typography>
            {enrollLoading && (
              <Typography sx={{ fontSize: 13, color: T.textFaint2 }}>Loading setup…</Typography>
            )}
            {!enrollLoading && enrollData?.qrcode && (
              <Box
                component="img"
                src={enrollData.qrcode}
                alt="Authenticator QR code"
                sx={{ display: 'block', maxWidth: '100%', alignSelf: 'center' }}
              />
            )}
            <TextField
              label="Authenticator code"
              type="text"
              fullWidth
              size="small"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEnrollConfirm();
              }}
              autoComplete="one-time-code"
              placeholder="Enter authenticator code"
              inputProps={{ 'data-testid': 'login-2fa-code' }}
              disabled={enrollLoading || !enrollData?.secret}
            />
            {localError && <Alert severity="error">{localError}</Alert>}
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={handleEnrollConfirm}
              disabled={loading || enrollLoading || !enrollData?.secret || !code}
              color="primary"
              data-testid="login-2fa-enroll-submit">
              {loading ? 'Loading...' : 'Confirm and sign in'}
            </Button>
          </Stack>
        )}

        {step === 'two-factor' && (
          <Stack spacing={2} sx={{ mt: 1 }} data-testid="two-factor-verify">
            <Typography sx={{ fontSize: 15, fontWeight: 900, color: T.text }}>
              Enter two-factor authentication code
            </Typography>
            <TextField
              label="Authentication code"
              type="text"
              fullWidth
              size="small"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTwoFactor();
              }}
              autoComplete="one-time-code"
              inputProps={{ 'data-testid': 'login-2fa-code' }}
            />
            {localError && <Alert severity="error">{localError}</Alert>}
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={handleTwoFactor}
              disabled={loading}
              color="primary"
              data-testid="login-2fa-verify-submit">
              {loading ? 'Loading...' : 'Check the code'}
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export default SignInScreen;
